import type { LamaReportConfidence } from "@playbookai/widget-schemas";

/**
 * Shared presentation vocabulary for the LAMA report dashboard widgets
 * (plan §13.3) — ported from the monolithic `LamaReportPage` so the eight
 * data-blind widget bodies keep the exact wording and scale the report page
 * shipped with. Pure constants/functions; no data access.
 */

/**
 * The LAMA maturity scale maximum. Single home for the places that must agree:
 * the progress bars' `max`, the previous-wave marker's offset, and the radar's
 * accessible "N-point scale" wording.
 */
export const LAMA_MATURITY_MAX = 5;

/**
 * DISPLAY CONVERSION of a 0–5 score to the percent scale (2026-09-10 ADR-0027
 * amendment, D8): score / 5 × 100, so 1 = 20% … 5 = 100% and a legitimate 0
 * is 0%. The v2 datasets still carry the 0–5 figures they were frozen with;
 * every LAMA widget converts at the point of display and rounds to a whole
 * percent there — never before. Linear, so a mean of scores and the mean of
 * their percents agree.
 */
export function lamaScoreToPercent(score: number): number {
  return (score / LAMA_MATURITY_MAX) * 100;
}

/** "62%" for a 0–5 score, or "—" for none. Whole percent at display only. */
export function lamaScorePercentLabel(
  score: number | null | undefined,
): string {
  return score == null ? "—" : `${Math.round(lamaScoreToPercent(score))}%`;
}

/** A 0–5 delta as whole percentage points, for a `TrendIndicator`. */
export function lamaDeltaPoints(delta: number): number {
  return Math.round(lamaScoreToPercent(delta));
}

export const LAMA_MODE_LABEL: Record<"baseline" | "self" | "inflow", string> = {
  baseline: "Baseline",
  self: "Self-assessment",
  inflow: "In-flow",
};

export const LAMA_CONFIDENCE_TONE: Record<
  LamaReportConfidence,
  "healthy" | "watch" | "critical"
> = {
  high: "healthy",
  medium: "watch",
  low: "critical",
};

const LAMA_CONFIDENCE_LABEL: Record<LamaReportConfidence, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

/**
 * Suppression copy (plan §15.3).
 *
 * THE RESPONDENT-COUNT THRESHOLD IS REMOVED (2026-09-15, Task 3 of
 * `docs/planning/lama-experience-simplification-plan.md`), so no LIVE dataset
 * sets `suppressed` any more and these two strings are effectively unreachable
 * on a current report. They are KEPT, and neither quotes a number any more:
 * `lamaSuppressedNote(minRespondents)` is deleted, because a sentence promising
 * "scores appear once at least 4 people respond" is exactly the hidden threshold
 * the removal rules out, and the field it read is gone from the dataset.
 *
 * What still reaches them is a FROZEN wave snapshot recorded under the old rule:
 * such a point stored no score and no rollups, so there is nothing to publish
 * for it whatever the current rule says. "Withheld to protect anonymity" and
 * "no data yet" therefore remain DIFFERENT facts and must never share a
 * sentence.
 */
export const LAMA_SUPPRESSED_TITLE = "Withheld to protect anonymity";

/** Suppression body copy. Names no threshold: there is none to name. */
export const LAMA_SUPPRESSED_BODY =
  "This wave's scores were withheld when it closed, under a minimum-respondents rule that no longer applies. Scores now appear as responses arrive.";

/**
 * "X of N people have responded" — the participation figure that REPLACED the
 * minimum-respondents promise (Task 3).
 *
 * DISTINCT PEOPLE on both sides: `responded` is people who have submitted at
 * least one assignment in the wave, `total` is the wave's frozen participant
 * set. Neither is an assignment, release or question count, and neither is
 * derivable from `participation`, which is a completion rate over work issued.
 */
export function lamaRespondedNote(responded: number, total: number): string {
  return `${responded} of ${total} ${total === 1 ? "person has" : "people have"} responded`;
}

/**
 * Open-wave marker (§13.4). A closed wave is immutable; an open one is a live
 * recomputation that moves as responses arrive, and a reader comparing two
 * numbers deserves to know which of them is still settling.
 */
export const LAMA_LIVE_WAVE_NOTE =
  "Live — this wave is still open, so these figures move as responses arrive.";

/**
 * Why the team heatmap is empty. Not "no data yet": the LAMA delivery spine
 * assigns waves to PEOPLE, and carries no team dimension at all, so there is
 * nothing to group by. Plan dependency PD5 owns adding one — until then the
 * honest copy names the missing capability instead of implying the data is
 * merely late.
 */
export const LAMA_TEAM_DIMENSION_NOTE =
  "Team scores appear once the delivery spine carries a team dimension — campaigns assign waves to people, not teams, so there is nothing to group by yet.";

/** Human label for a confidence band. */
export function lamaConfidenceLabel(confidence: LamaReportConfidence): string {
  return LAMA_CONFIDENCE_LABEL[confidence];
}

/**
 * A movement too small to reach one whole percentage point, worded rather than
 * rounded away.
 *
 * `lamaDeltaPoints` rounds, and the movers lists are built from `delta > 0` /
 * `delta < 0` with no magnitude floor — so a question that moved 3.00 → 3.01
 * is a real entry in **Most improved** whose rounded delta is `0`. Rendered
 * through `TrendIndicator` that became a flat grey dash reading "0 pts", with
 * an accessible name of "No change from the previous wave", inside a column
 * headed "Most improved": the row contradicted its own heading twice.
 *
 * Null when the movement DOES reach a point, so a caller falls through to the
 * ordinary indicator. Never used to hide a row — which questions appear is the
 * server's answer, and this only changes how a published one reads.
 */
export function lamaSubPointDeltaLabel(delta: number): string | null {
  if (delta === 0 || lamaDeltaPoints(delta) !== 0) return null;
  return delta > 0 ? "<1 pt" : "−<1 pt";
}

/** The accessible reading of that same sub-point movement. */
export function lamaSubPointDeltaDescription(delta: number): string {
  return `${delta > 0 ? "Increase" : "Decrease"} of less than one point from the previous wave`;
}

/**
 * Every caller passes a **previous-wave** delta (`latest.overall - previous.overall`
 * for the headline, the same shape for pillar rows and movers), so the label says
 * so. It previously said "from baseline", which on a six-wave report announced a
 * one-wave move of +0.05 as the whole change since baseline — while the true
 * since-baseline figure was computed separately and rendered right beside it, as
 * different text a sighted reader could see and a screen-reader user could not.
 */
export function lamaDeltaLabel(delta: number): string {
  const points = Math.abs(lamaDeltaPoints(delta));
  if (points === 0) return "No change from the previous wave";
  return `${delta > 0 ? "Increase" : "Decrease"} of ${points} ${
    points === 1 ? "point" : "points"
  } from the previous wave`;
}

/**
 * Participation as a numerator over a denominator (PB-191 Task 20).
 *
 * A percentage on its own is not an honest denominator: "50%" reads identically
 * for 1 of 2 and 40 of 80, and only one of those carries weight. Both ship, so a
 * reader can judge the figure rather than trust it.
 *
 * ASSIGNMENT ROWS, NEVER PEOPLE. An assignment is person x release (ADR-0057), so
 * these are a completion rate and NOT a population count — which is why they are
 * safe to show on a suppressed wave, where a score is not.
 */
export function lamaParticipationDetail(
  submitted: number,
  total: number,
): string {
  return `${submitted} of ${total} ${total === 1 ? "response" : "responses"}`;
}

/**
 * A category's per-wave history as text (PB-191 Task 21).
 *
 * THE SPARKLINE IS `aria-hidden`, so before this a screen-reader user got NO
 * history at all — only the current score and a delta against one prior wave.
 * This is the same series, readable.
 *
 * An unmeasured wave says so by NAME rather than being skipped. Skipping is what
 * `spark` does, and it is why a gap currently renders as a continuous line: a
 * category in W1 and W3 but not W2 reads as two consecutive points.
 */
export function lamaCategoryHistoryLabel(
  category: string,
  history: readonly { code: string; score: number | null; live: boolean }[],
): string {
  const parts = history.map((point) =>
    point.score === null
      ? `${point.code} not measured`
      : `${point.code} ${lamaScorePercentLabel(point.score)}${point.live ? " (live)" : ""}`,
  );
  return `${category} by wave: ${parts.join(", ")}.`;
}

/**
 * Whether a history has an INTERIOR gap — an unmeasured wave with measured waves
 * on both sides.
 *
 * Only an interior gap makes the sparkline lie. Leading nulls (the category was
 * introduced later) and trailing ones (it was dropped) shorten the line without
 * misrepresenting what it spans; an interior one silently closes a hole.
 */
export function lamaHistoryHasGap(
  history: readonly { score: number | null }[],
): boolean {
  const first = history.findIndex((point) => point.score !== null);
  if (first === -1) return false;
  let last = history.length - 1;
  while (last > first && history[last]?.score === null) last -= 1;
  return history.slice(first, last + 1).some((point) => point.score === null);
}
